import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { getDocs, collection, query, orderBy, limit, DocumentData } from 'firebase/firestore';
import { db } from '@/firebase';
import { useNavigate } from 'react-router-dom';
import { Trophy } from 'lucide-react';

interface LeaderboardWidgetProps {
  householdId: string | null;
  currentUserId: string;
}

interface MemberPointEntry {
  userId: string;
  monthlyPoints: number;
  totalPoints: number;
  displayName: string;
}

const LeaderboardWidget: React.FC<LeaderboardWidgetProps> = ({ householdId, currentUserId }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [entries, setEntries] = useState<MemberPointEntry[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!householdId) return;

    const fetchData = async () => {
      setLoading(true);
      try {
        const memberPointsRef = collection(db, 'households', householdId, 'memberPoints');
        const membersRef = collection(db, 'households', householdId, 'members');
        const pointsQuery = query(memberPointsRef, orderBy('monthlyPoints', 'desc'), limit(8));
        const [pointsSnap, membersSnap] = await Promise.all([
          getDocs(pointsQuery),
          getDocs(membersRef)
        ]);
        const membersMap = new Map<string, DocumentData>();
        membersSnap.forEach((doc) => {
          membersMap.set(doc.id, doc.data());
        });
        const mergedData: MemberPointEntry[] = pointsSnap.docs.map((doc) => {
          const data = doc.data();
          const memberInfo = membersMap.get(data.userId);
          return {
            userId: data.userId,
            monthlyPoints: data.monthlyPoints || 0,
            totalPoints: data.totalPoints || 0,
            displayName: memberInfo?.displayName || "Member"
          };
        });
        setEntries(mergedData);
      } catch (error) {
        console.error("Error fetching leaderboard data:", error);
        setEntries([]);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [householdId]);

  if (!householdId) return null;

  const getRankIcon = (index: number) => {
    if (index === 0) return "🥇";
    if (index === 1) return "🥈";
    if (index === 2) return "🥉";
    return <span className="text-slate-500 text-xs w-5 text-center">{index + 1}</span>;
  };

  return (
    <div className="h-full flex flex-col bg-white dark:bg-slate-900 rounded-2xl p-5 space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Trophy className="w-4 h-4 text-yellow-500" />
          <span className="text-xs font--black uppercase tracking-widest text-slate-400">{t('achievements.leaderboard_title', 'Leaderboard')}</span>
        </div>
        <span className="text-slate-500 text-xs font-medium">This Month</span>
      </div>

      <div className="flex-1 overflow-hidden flex flex-col">
        {loading ? (
          <div className="space-y-2 mt-1">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="h-8 bg-slate-100 dark:bg-slate-800 rounded-lg animate-pulse" />
            ))}
          </div>
        ) : entries.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center text-center space-y-2 py-4">
            <Trophy className="w-8 h-8 text-slate-200 dark:text-slate-700" />
            <p className="text-sm text-slate-400">{t('achievements.no_activity_month', 'No activity recorded this month')}</p>
          </div>
        ) : (
          <div className="space-y-1 overflow-y-auto pr-1 custom-scrollbar">
            {entries.map((entry, index) => {
              const isCurrentUser = entry.userId === currentUserId;
              return (
                <div
                  key={entry.userId}
                  className={`flex items-center gap-3 p-2 transition-colors ${
                    isCurrentUser ? "bg-brand/10 dark:bg-brand/20 rounded-lg px-2 -mx-2" : ""
                  }`}
                >
                  <div className="w-5 flex justify-center text-5m">
                    {typeof getRankIcon(index) === 'string' ? (
                      <span>{getRankIcon(index)}</span>
                    ) : (
                      getRankIcon(index)
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-slate-900 dark:text-white text-sm font-medium truncate">
                      {entry.displayName}
                      {isCurrentUser && <span className="text-brand dark:text-indigo-400 text-xs ml-1">(You)</span>}
                    </p>
                  </div>
                  <div className="text-slate-400 text-xs font-medium">
                    {entry.monthlyPoints} pts
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div className="mt-auto pt-2 border-t border-slate-100 dark:border-slate-800">
        <button
          onClick={() => navigate('/tasks')}
          className="text-brand dark:text-indigo-400 text-xs font-medium hover:text-indigo-300 transition-colors flex items-center gap-1"
        >
          {t('achievements.view_details', 'View Details')} →
        </button>
      </div>
    </div>
  );
};

export default LeaderboardWidget;